/** Data hooks for the Agents screens. */

import { useMutation, useQueries, useQuery } from '@tanstack/react-query'
import { queryClient } from '@/lib/query.client'
import { agentsApi } from './agents.api'
import type { AgentDraft, AskBody } from './types'

export const agentKeys = {
  all: ['agents'] as const,
  options: ['agents', 'options'] as const,
  one: (id: string) => ['agents', 'one', id] as const,
  connectionModels: (connectionId: string) =>
    ['agents', 'connection-models', connectionId] as const,
  threads: (id: string) => ['agents', 'threads', id] as const,
  thread: (id: string, threadId: string) => ['agents', 'thread', id, threadId] as const,
}

export function useAgents() {
  return useQuery({ queryKey: agentKeys.all, queryFn: agentsApi.list })
}

export function useAgentOptions() {
  return useQuery({
    queryKey: agentKeys.options,
    queryFn: agentsApi.options,
    // The model list is fetched from the provider; it does not move between
    // two dialogs opened a minute apart.
    staleTime: 60_000,
  })
}

/**
 * The models every connection can call, one query each, all at once.
 *
 * The dialog's picker lists each connection's models under its own heading
 * the way the Chat page does, and a heading that fills in a beat after the
 * menu opens is one people miss. There are rarely more than a couple.
 */
export function useConnectionsModels(connectionIds: string[]) {
  return useQueries({
    queries: connectionIds.map((id) => ({
      queryKey: agentKeys.connectionModels(id),
      queryFn: () => agentsApi.connectionModels(id),
      staleTime: 60_000,
    })),
  })
}

export function useAgent(id: string | undefined) {
  return useQuery({
    queryKey: agentKeys.one(id ?? ''),
    queryFn: () => agentsApi.get(id!),
    enabled: Boolean(id),
  })
}

export function useCreateAgent() {
  return useMutation({
    mutationFn: (body: AgentDraft) => agentsApi.create(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agentKeys.all }),
  })
}

export function useUpdateAgent(id: string) {
  return useMutation({
    mutationFn: (body: Partial<AgentDraft> & { is_active?: boolean }) =>
      agentsApi.update(id, body),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: agentKeys.all }),
        queryClient.invalidateQueries({ queryKey: agentKeys.one(id) }),
      ]),
  })
}

export function useDeleteAgent() {
  return useMutation({
    mutationFn: (id: string) => agentsApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agentKeys.all }),
  })
}

export function useThreads(id: string | undefined) {
  return useQuery({
    queryKey: agentKeys.threads(id ?? ''),
    queryFn: () => agentsApi.threads(id!),
    enabled: Boolean(id),
  })
}

export function useThread(id: string | undefined, threadId: string | undefined) {
  return useQuery({
    queryKey: agentKeys.thread(id ?? '', threadId ?? ''),
    queryFn: () => agentsApi.thread(id!, threadId!),
    enabled: Boolean(id && threadId),
  })
}

export function useAskAgent(id: string) {
  return useMutation({
    mutationFn: (body: AskBody) => agentsApi.ask(id, body),
    onSuccess: (answer) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: agentKeys.threads(id) }),
        queryClient.invalidateQueries({
          queryKey: agentKeys.thread(id, answer.thread.thread_id),
        }),
        queryClient.invalidateQueries({ queryKey: agentKeys.all }),
      ]),
  })
}

export function useDeleteThread(id: string) {
  return useMutation({
    mutationFn: (threadId: string) => agentsApi.deleteThread(id, threadId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agentKeys.threads(id) }),
  })
}

export function useRenameThread(id: string) {
  return useMutation({
    mutationFn: ({ threadId, title }: { threadId: string; title: string }) =>
      agentsApi.renameThread(id, threadId, title),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: agentKeys.threads(id) }),
  })
}
